import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageSquare, 
  Plus, 
  Edit2, 
  Check, 
  X, 
  Trash2,
  Search,
  Clock
} from 'lucide-react';
import { Conversation } from '../../types';
import { Button } from '@/components/ui';

interface ConversationHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  conversations: Conversation[];
  selectedConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
  onNewConversation: () => void;
  onRenameConversation: (conversationId: string, newName: string) => void;
  onDeleteConversation?: (conversationId: string) => void;
  isLoading?: boolean;
}

const ConversationHistoryPanel: React.FC<ConversationHistoryPanelProps> = ({
  isOpen,
  onClose,
  conversations,
  selectedConversationId,
  onSelectConversation,
  onNewConversation,
  onRenameConversation,
  onDeleteConversation,
  isLoading = false,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Filtrar conversaciones por búsqueda
  const filteredConversations = conversations.filter(conv =>
    (conv.title || conv.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Iniciar edición
  const startEdit = (conversation: Conversation) => {
    setEditingId(conversation.id);
    setEditValue(conversation.title || conversation.name || '');
  };

  // Guardar edición
  const saveEdit = () => {
    if (editingId && editValue.trim()) {
      const conversation = conversations.find(c => c.id === editingId);
      if (conversation) {
        onRenameConversation(conversation.conversationId, editValue.trim());
      }
    }
    setEditingId(null);
    setEditValue('');
  };

  // Cancelar edición
  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  // Focus en input cuando se inicia edición
  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  // Formatear fecha
  const formatDate = (date: Date | string | number) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return 'Hoy';
    } else if (days === 1) {
      return 'Ayer';
    } else if (days < 7) {
      return `Hace ${days} días`;
    } else {
      return new Date(date).toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 transition-opacity"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative flex flex-col w-80 bg-white shadow-xl ml-auto h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-primary-600 text-white">
          <h3 className="font-semibold text-sm">Historial de Chats</h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-primary-700 transition-colors"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar conversaciones..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* New Conversation Button */}
        <div className="p-3 border-b border-gray-200">
          <Button
            onClick={onNewConversation}
            variant="primary"
            size="sm"
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nueva Conversación
          </Button>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <MessageSquare className="h-12 w-12 text-gray-300 mb-4" />
              <p className="text-sm text-gray-500">
                {searchQuery ? 'No se encontraron conversaciones' : 'No hay conversaciones'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredConversations.map((conversation) => {
                const isSelected = conversation.conversationId === selectedConversationId;
                const isEditing = editingId === conversation.id;

                return (
                  <div
                    key={conversation.id}
                    className={`group p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                      isSelected ? 'bg-primary-50 border-l-4 border-primary-600' : ''
                    }`}
                    onClick={() => !isEditing && onSelectConversation(conversation.conversationId)}
                  >
                    {isEditing ? (
                      <div className="flex items-center space-x-2">
                        <input
                          ref={inputRef}
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEdit();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          className="flex-1 px-2 py-1 text-sm border border-primary-500 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            saveEdit();
                          }}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelEdit();
                          }}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {conversation.title || conversation.name || 'Nueva Conversación'}
                            </h4>
                            <div className="flex items-center space-x-1 ml-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEdit(conversation);
                                }}
                                className="p-1 text-gray-400 hover:text-primary-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Renombrar"
                              >
                                <Edit2 className="h-3 w-3" />
                              </button>
                              {onDeleteConversation && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (confirm('¿Eliminar esta conversación?')) {
                                      onDeleteConversation(conversation.conversationId);
                                    }
                                  }}
                                  className="p-1 text-gray-400 hover:text-red-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Eliminar"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          </div>
                          {conversation.lastMessage && (
                            <p className="text-xs text-gray-500 truncate mb-1">
                              {conversation.lastMessage}
                            </p>
                          )}
                          <div className="flex items-center space-x-2 text-xs text-gray-400">
                            <Clock className="h-3 w-3" />
                            <span>
                              {conversation.lastMessageAt
                                ? formatDate(
                                    typeof conversation.lastMessageAt === 'string' || typeof conversation.lastMessageAt === 'number'
                                      ? new Date(conversation.lastMessageAt)
                                      : conversation.lastMessageAt
                                  )
                                : formatDate(
                                    typeof conversation.createdAt === 'string' || typeof conversation.createdAt === 'number'
                                      ? new Date(conversation.createdAt)
                                      : conversation.createdAt
                                  )}
                            </span>
                            {conversation.messageCount !== undefined && (
                              <>
                                <span>•</span>
                                <span>{conversation.messageCount} mensajes</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationHistoryPanel;

